import React, { useState, useEffect } from "react";
import {  Button,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Print as PrintIcon,
  DirectionsBoat,
  Inventory,
  LocationOn,
  CalendarToday,
  SelectAll,
  Layers,
  Description,
} from "@mui/icons-material";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";
import { renderTankCertificateV2 } from "../../print-v2";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";
import { getVoyageNumber } from "../../utils/domainSelectors";

const CleanlinessTankCertification = () => {
  const { t } = useTranslation("tankcert");
  const theme = useTheme();
  const { selectedTripId, selectedCargoIndex, currentVoyage } =
    useVoyageContextV2();
  const { companyLogo } = useCompanyContext();
  const { is24Hour } = useSettings();
  const { tanks } = useShipSettings();

  const [loading, setLoading] = useState(true);
  const [checkedTanks, setCheckedTanks] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  useEffect(() => {
    if (tanks && tanks.length > 0) {
      const initialState = Object.fromEntries(tanks.map((tank) => [tank.name, false]));
      setCheckedTanks(initialState);
    }
  }, [tanks]);

  useEffect(() => {
    if (currentVoyage) setLoading(false);
  }, [currentVoyage]);

  const handleToggleAllTanks = () => {
    const allChecked = Object.values(checkedTanks).every(Boolean);
    const newState = Object.keys(checkedTanks).reduce((acc, tank) => {
      acc[tank] = !allChecked;
      return acc;
    }, {});
    setCheckedTanks(newState);
  };

  const handlePrintButton = () => {
    const selectedCargo = getSelectedCargo(
      currentVoyage,
      selectedTripId,
      selectedCargoIndex
    );

    if (!selectedCargo) {
      setSnackbar({
        open: true,
        message: "Please select a cargo before printing.",
        severity: "warning",
      });
      return;
    }

    const selectedTanksStr = Object.keys(checkedTanks)
      .filter((tank) => checkedTanks[tank])
      .join(", ");

    if (!selectedTanksStr) {
      setSnackbar({
        open: true,
        message: "Please select at least one tank before printing.",
        severity: "warning",
      });
      return;
    }

    renderTankCertificateV2({
      currentVoyage,
      selectedTripId,
      selectedCargoIndex,
      selectedTanks: selectedTanksStr,
      companyLogo,
      isCTC: true,
      is24Hour,
      t,
    });
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  const trip = getSelectedTrip(currentVoyage, selectedTripId);
  const cargo = getSelectedCargo(
    currentVoyage,
    selectedTripId,
    selectedCargoIndex
  );

  const InfoItem = ({ icon, label, value }) => (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Box sx={{ color: "primary.main", mt: 0.5 }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ fontWeight: 700, textTransform: "uppercase" }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="600">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );

  const tankListP = Object.keys(checkedTanks)
    .filter((t) => t.endsWith("P"))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const tankListS = Object.keys(checkedTanks)
    .filter((t) => t.endsWith("S"))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pb: 5 }}>
      <Typography variant="h4" fontWeight="900" sx={{ mb: 1 }}>
        {t("titleCTC")}
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
        Verify voyage details and certify tank cleanliness for the upcoming
        cargo.
      </Typography>

      <Stack spacing={3}>
        {/* 1. Voyage Summary */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<DirectionsBoat />}
                label={t("voyageInfo.vessel")}
                value={currentVoyage?.vessel}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<Description />}
                label={t("voyageInfo.voyage")}
                value={getVoyageNumber(currentVoyage)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<Inventory />}
                label={t("voyageInfo.cargo")}
                value={`${cargo?.cargo} (${cargo?.quantity} ${cargo?.unit})`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<LocationOn />}
                label={t("voyageInfo.port")}
                value={`${trip?.to} (${trip?.jetty || "N/A"})`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <InfoItem
                icon={<CalendarToday />}
                label="Inspection"
                value={
                  cargo?.activities?.["tank-inspection"]
                    ? `${cargo.activities["tank-inspection"].date} ${cargo.activities["tank-inspection"].time}`
                    : "Not in SoF"
                }
              />
            </Grid>
          </Grid>
        </Paper>

        {/* 2. Horizontal Tank Selection */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Layers fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="800">
                CLEANLINESS SELECTION
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="text"
              startIcon={<SelectAll />}
              onClick={handleToggleAllTanks}
              sx={{ fontWeight: 700 }}
            >
              {Object.values(checkedTanks).every(Boolean)
                ? t("voyageInfo.uncheckAllTanks")
                : t("voyageInfo.checkAllTanks")}
            </Button>
          </Box>

          <Box sx={{ p: 3 }}>
            <Stack spacing={4}>
              {/* Port Side Row */}
              <Box>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    fontWeight: 900,
                    mb: 1.5,
                    display: "block",
                    letterSpacing: 1,
                  }}
                >
                  PORT SIDE
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {tankListP.map((tank) => (
                    <FormControlLabel
                      key={tank}
                      control={
                        <Checkbox
                          size="small"
                          checked={checkedTanks[tank]}
                          onChange={(e) =>
                            setCheckedTanks((prev) => ({
                              ...prev,
                              [tank]: e.target.checked,
                            }))
                          }
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          fontWeight={checkedTanks[tank] ? 700 : 400}
                        >
                          {tank}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        p: "4px 12px 4px 4px",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: checkedTanks[tank]
                          ? "primary.main"
                          : "divider",
                        bgcolor: checkedTanks[tank]
                          ? alpha(theme.palette.primary.main, 0.05)
                          : "transparent",
                        minWidth: "95px",
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider />

              {/* Starboard Side Row */}
              <Box>
                <Typography
                  variant="caption"
                  color="secondary"
                  sx={{
                    fontWeight: 900,
                    mb: 1.5,
                    display: "block",
                    letterSpacing: 1,
                  }}
                >
                  STARBOARD SIDE
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {tankListS.map((tank) => (
                    <FormControlLabel
                      key={tank}
                      control={
                        <Checkbox
                          size="small"
                          color="secondary"
                          checked={checkedTanks[tank]}
                          onChange={(e) =>
                            setCheckedTanks((prev) => ({
                              ...prev,
                              [tank]: e.target.checked,
                            }))
                          }
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          fontWeight={checkedTanks[tank] ? 700 : 400}
                        >
                          {tank}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        p: "4px 12px 4px 4px",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: checkedTanks[tank]
                          ? "secondary.main"
                          : "divider",
                        bgcolor: checkedTanks[tank]
                          ? alpha(theme.palette.secondary.main, 0.05)
                          : "transparent",
                        minWidth: "95px",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Stack>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<PrintIcon />}
          onClick={handlePrintButton}
          sx={{ py: 2, px: 10, borderRadius: 3, fontWeight: 800 }}
        >
          {t("printCTC")}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CleanlinessTankCertification;
